import { forwardRef, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const iconButtonVariants = cva(
  'inline-flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary',
        outline:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-primary',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-primary',
        success: 'text-green-600 hover:bg-green-50 dark:hover:bg-green-950 focus:ring-green-500',
        muted: 'text-muted-foreground hover:bg-muted hover:text-foreground focus:ring-muted',
      },
      size: {
        sm: 'p-2.5 sm:p-1.5', // 44px minimum on mobile (icon + padding), smaller on desktop
        md: 'p-3 sm:p-2', // 48px minimum on mobile, 40px on desktop
        lg: 'p-3.5 sm:p-3', // 52px minimum on mobile, 48px on desktop
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'md',
    },
  }
);

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  icon: ReactNode;
  'aria-label': string;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, icon, 'aria-label': ariaLabel, ...props }, ref) => {
    return (
      <button
        className={cn(iconButtonVariants({ variant, size }), className)}
        ref={ref}
        aria-label={ariaLabel}
        {...props}
      >
        {icon}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export { IconButton };
